import { useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { Select, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { usePersistedState } from '../../../lib/usePersistedState';
import { channelLabel, ticketErrorText } from '../lib/data';

type Mode = 'reply' | 'note';

/**
 * Reply goes to the client on a linked conversation; a note stays inside the team. When the channel's
 * messaging window has closed, the reply cannot go as text: say so and send them to the inbox, where a template
 * still reaches the client.
 */
export function Composer({ ticket, onSent, onLink }: { ticket: tickets.Ticket; onSent: () => void; onLink: () => void }) {
  const t = useT();
  const nav = useNavigate();
  const [mode, setMode] = useState<Mode>('reply');
  const [drafts, setDrafts] = usePersistedState<Record<string, string>>('tenant.tickets.drafts', {});
  const key = `${ticket.id}:${mode}`;
  const text = drafts[key] ?? '';
  const setText = (v: string) => setDrafts((d) => {
    const next = { ...d };
    if (v) next[key] = v; else delete next[key];
    return next;
  });
  const convs = ticket.conversations;
  const [convId, setConvId] = useState('');
  const target = convId || convs[convs.length - 1]?.id || '';
  const [windowClosedOn, setWindowClosedOn] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => (mode === 'reply'
      ? tickets.replyToTicket(ticket.id, { text: text.trim(), conversation_id: target || undefined })
      : tickets.addTicketNote(ticket.id, text.trim())),
    onSuccess: () => {
      setText('');
      setWindowClosedOn(null);
      notifySuccess(mode === 'reply' ? t('tickets.composer.sent') : t('tickets.composer.noted'), ticket.number);
      onSent();
    },
    onError: (e) => {
      if (tickets.ticketErrorCode(e) === tickets.MESSAGING_WINDOW_CLOSED) setWindowClosedOn(target || null);
    }
  });

  const merged = !!ticket.merged_into_id;
  const canSend = !!text.trim() && !send.isPending && !merged && (mode === 'note' || convs.length > 0);
  const windowClosed = mode === 'reply' && windowClosedOn !== null && windowClosedOn === target;
  const otherError = send.error && tickets.ticketErrorCode(send.error) !== tickets.MESSAGING_WINDOW_CLOSED ? send.error : null;

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSend) {
      e.preventDefault();
      send.mutate();
    }
  }

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Tabs value={mode} onChange={(v) => { setMode(v as Mode); send.reset(); }}>
          <Tabs.List>
            <Tabs.Tab value="reply">{t('tickets.composer.reply')}</Tabs.Tab>
            <Tabs.Tab value="note">{t('tickets.composer.note')}</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {mode === 'reply' && convs.length === 0 ? (
          <Alert color="info">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{t('tickets.error.NO_CONVERSATION')}</span>
              <Button size="xs" variant="default" onClick={onLink}>{t('tickets.link.action')}</Button>
            </Group>
          </Alert>
        ) : (
          <>
            {mode === 'reply' && convs.length > 1 && (
              <Select
                size="sm"
                label={t('tickets.composer.on')}
                value={target}
                onChange={(e) => setConvId(e.currentTarget.value)}
                data={convs.map((c, i) => ({ value: c.id, label: t('tickets.composer.conversation', { channel: channelLabel(t, c.channel), n: i + 1 }) }))}
              />
            )}
            <Textarea
              aria-label={mode === 'reply' ? t('tickets.composer.reply') : t('tickets.composer.note')}
              placeholder={mode === 'reply' ? t('tickets.composer.replyPlaceholder') : t('tickets.composer.notePlaceholder')}
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              onKeyDown={onKeyDown}
              minRows={3}
              maxLength={4000}
              disabled={merged}
              className={mode === 'note' ? 'rr-tickets__note-input' : undefined}
              description={mode === 'reply' ? t('tickets.composer.replyHint') : t('tickets.composer.noteHint')}
            />
            {windowClosed && (
              <Alert color="warning" role="alert" data-testid="window-closed">
                <Stack gap="xs">
                  <Text role="bodySmall" fw={600}>{t('tickets.composer.windowClosed.title')}</Text>
                  <Text role="bodySmall">{t('tickets.composer.windowClosed.body')}</Text>
                  <div>
                    <Button size="xs" variant="default" onClick={() => nav(`/inbox?conversation=${encodeURIComponent(target)}`)}>
                      {t('tickets.composer.windowClosed.action')}
                    </Button>
                  </div>
                </Stack>
              </Alert>
            )}
            {otherError && <Alert color="error" role="alert">{ticketErrorText(t, otherError)}</Alert>}
            <Group gap="sm" justify="flex-end">
              <Button size="sm" loading={send.isPending} disabled={!canSend} onClick={() => send.mutate()}>
                {mode === 'reply' ? t('tickets.composer.send') : t('tickets.composer.addNote')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
}
